<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { AlertDialogRoot, AlertDialogPortal, AlertDialogContent, AlertDialogTitle, AlertDialogDescription } from 'reka-ui'
import { useAuthPopupRecovery } from '@/composables/useAuthPopupRecovery'
import {
  AUTH_FAILURE_REDIRECT_EVENT,
  startCasLoginRedirect,
  type AuthFailureRedirectEventDetail,
} from '@/auth/session'
import { isEmbeddedWindow } from '@/utils/windowContext'
import { authenticationRecovery } from '@/auth/recovery'
import {
  authSurfaceOf,
  casRedirectHandlesAuthFailure,
  isFramedDesktopBotRoute,
} from '@/auth/authSurfacePolicy'

const props = withDefaults(defineProps<{
  autoStartPopupOnShow?: boolean
  enabled?: boolean
}>(), {
  autoStartPopupOnShow: false,
  enabled: true,
})

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const botName = computed(() => typeof route.params.botName === 'string' ? route.params.botName : null)
const authSurface = computed(() => authSurfaceOf(route.meta))
const isIframeHostedDesktopBot = computed(() =>
  isFramedDesktopBotRoute(authSurface.value, route.name),
)
const {
  loginInProgress,
  popupBlocked,
  loginError,
  startLoginPopup,
  cancelLoginPopup,
} = useAuthPopupRecovery()

const recoveryReason = computed(() => {
  const state = authenticationRecovery.state.value
  return state.phase === 'idle' ? 'sign_in' : state.reason
})
const showAuthGate = computed(() => props.enabled && authenticationRecovery.blocked.value
  && !casRedirectHandlesAuthFailure(recoveryReason.value, authSurface.value))
const gateTitle = computed(() =>
  t(recoveryReason.value === 'sign_in' ? 'auth.voluntaryTitle' : recoveryReason.value === 'expired' ? 'auth.expiredTitle' : 'auth.requiredTitle'),
)
const gateDescription = computed(() =>
  t(recoveryReason.value === 'sign_in' ? 'auth.voluntaryDescription' : recoveryReason.value === 'expired' ? 'auth.expiredDescription' : 'auth.requiredDescription'),
)
const canRedirectInThisTab = computed(() => authSurface.value === 'full' && !isEmbeddedWindow())
const standaloneChatHref = computed(() => {
  if (botName.value) {
    return router.resolve({ name: 'bot-name', params: { botName: botName.value } }).href
  }
  return router.resolve({ name: 'landing' }).href
})

// Every surface recovers through the popup: a full-page redirect to CAS would
// throw away the transcript, the scroll position and whatever the user typed.
// The exception is a first sign-in on an unframed desktop surface, where the
// redirect handler is already navigating away and an overlay would only flash.
function handleAuthFailureEvent(event: Event) {
  const detail = (event as CustomEvent<AuthFailureRedirectEventDetail>).detail
  if (loginInProgress.value || authenticationRecovery.state.value.phase !== 'required') return
  if (casRedirectHandlesAuthFailure(detail.reason, authSurface.value)) return
  if (isIframeHostedDesktopBot.value) startLogin()
}

function startLogin() {
  startLoginPopup(route.fullPath)
}

onMounted(() => {
  window.addEventListener(AUTH_FAILURE_REDIRECT_EVENT, handleAuthFailureEvent)
})

onUnmounted(() => {
  window.removeEventListener(AUTH_FAILURE_REDIRECT_EVENT, handleAuthFailureEvent)
})

watch(
  () => showAuthGate.value,
  (isVisible, wasVisible) => {
    if (!props.autoStartPopupOnShow) return
    if (!isVisible || wasVisible) return
    if (loginInProgress.value) return
    startLogin()
  },
)
</script>

<template>
  <AlertDialogRoot :open="showAuthGate">
    <AlertDialogPortal>
      <AlertDialogContent
        class="fixed inset-0 z-auth-overlay pointer-events-auto flex items-center justify-center bg-slate-950/30 px-6"
        :class="{ 'bg-slate-950/40': authSurface === 'full' }"
      >
        <div class="w-full max-w-md rounded-3xl border border-sky-200 bg-sky-50/95 p-7 text-center shadow-lg">
          <p class="text-xs font-semibold uppercase tracking-[0.3em] text-sky-700">
            {{ t('auth.overlayLabel') }}
          </p>
          <AlertDialogTitle as="h1" class="mt-3 text-2xl font-semibold text-slate-950">{{ gateTitle }}</AlertDialogTitle>
          <AlertDialogDescription class="mt-3 text-sm leading-6 text-slate-700">{{ gateDescription }}</AlertDialogDescription>
          <p
            v-if="loginError"
            class="mt-3 text-xs text-red-700"
          >
            {{ loginError }}
          </p>
          <p v-if="popupBlocked && canRedirectInThisTab" class="mt-3 text-xs text-slate-700">
            {{ t('auth.redirectDraftNotice') }}
          </p>
          <div class="mt-5 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              class="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800"
              :disabled="loginInProgress"
              @click="startLogin"
            >
              {{ loginInProgress ? t('auth.waitingForSignIn') : popupBlocked ? t('auth.retryPopup') : t('auth.signInWithPopup') }}
            </button>
            <button
              v-if="loginInProgress || loginError"
              type="button"
              class="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-900 transition hover:bg-slate-100"
              @click="cancelLoginPopup"
            >
              {{ t('auth.cancelSignIn') }}
            </button>
            <button
              v-if="popupBlocked && canRedirectInThisTab"
              type="button"
              class="rounded-full bg-slate-950 px-5 py-2.5 text-sm font-medium text-white"
              @click="startCasLoginRedirect(route.fullPath)"
            >
              {{ t('auth.continueInThisTab') }}
            </button>
            <a
              v-if="!canRedirectInThisTab"
              :href="standaloneChatHref"
              class="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-900 transition hover:bg-slate-100"
            >
              {{ t('auth.openChatInThisTab') }}
            </a>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialogPortal>
  </AlertDialogRoot>
</template>
